/**
 * Datenschutzerklärung – DSGVO Art. 13 / 14
 *
 * Inhalt aus prompts/DATENSCHUTZ_LIVE_FASSUNG.md übernommen (finalisierte Fassung,
 * Stand 09.07.2026). Bei inhaltlichen Änderungen künftig zuerst dort pflegen und
 * dann hierher übertragen, damit beide Fassungen synchron bleiben.
 */

import '../styles/legal.css';
import { renderLegalPage, type LegalSection } from './legal-helper';

/** Datenschutzerklärung-Seite rendern. */
export function renderDatenschutz(): void {
  const sections: LegalSection[] = [
    {
      id: 'section-1',
      title: '§ 1 Verantwortlicher',
      defaultExpanded: true,
      content: `
        <p>Verantwortlicher im Sinne des Art. 4 Nr. 7 DSGVO:</p>
        <p>
          <strong>Aridara Studios</strong><br />
          Inhaber: Chris Wiedemaier<br />
          Höhenweg 12<br />
          58739 Wickede (Ruhr)<br />
          Deutschland
        </p>
        <p>E-Mail: <a href="mailto:legal@nighttrade.de">legal@nighttrade.de</a></p>
        <p>
          Dienst: Nighttrade<br />
          Website: <a href="https://nighttrade.de" target="_blank" rel="noopener noreferrer">https://nighttrade.de</a><br />
          Alpha-Umgebung: <a href="https://alpha.nighttrade.de" target="_blank" rel="noopener noreferrer">https://alpha.nighttrade.de</a>
        </p>
      `
    },
    {
      id: 'section-2',
      title: '§ 2 Allgemeine Grundsätze der Datenverarbeitung',
      defaultExpanded: false,
      content: `
        <p>
          <strong>2.1</strong> Nighttrade verarbeitet personenbezogene Daten der Nutzer nur, soweit
          dies für Bereitstellung, Sicherheit, Betrieb und Abrechnung des Dienstes erforderlich ist,
          eine gesetzliche Pflicht besteht oder eine Einwilligung des Nutzers vorliegt.
        </p>
        <p><strong>2.2</strong> Rechtsgrundlagen im Einzelnen:</p>
        <ul>
          <li>Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung (Bereitstellung des Spiels)</li>
          <li>Art. 6 Abs. 1 lit. f DSGVO — berechtigtes Interesse (u. a. Missbrauchsschutz, Betriebssicherheit)</li>
          <li>Art. 6 Abs. 1 lit. c DSGVO — gesetzliche Verpflichtungen (u. a. steuerrechtliche Aufbewahrung)</li>
          <li>Art. 6 Abs. 1 lit. a DSGVO — Einwilligung (u. a. Twitch-Verknüpfung)</li>
        </ul>
        <p>
          <strong>2.3</strong> Nighttrade folgt dem Grundsatz der Datenminimierung (Art. 5 Abs. 1 lit. c
          DSGVO): Wo technisch möglich, werden personenbezogene Daten nicht im Klartext, sondern als
          nicht umkehrbarer Hash gespeichert oder nach Zweckerfüllung automatisch gelöscht.
          Einzelheiten in § 4 und § 8.
        </p>
      `
    },
    {
      id: 'section-3',
      title: '§ 3 Hosting und Server-Protokolle',
      defaultExpanded: false,
      content: `
        <p>
          <strong>3.1</strong> Nighttrade wird auf Servern der netcup GmbH (Deutschland) betrieben.
          Alle Spiel- und Kontodaten werden auf Servern in Deutschland gespeichert.
        </p>
        <p>
          <strong>3.2</strong> Bei jedem Aufruf werden automatisch folgende technische Daten in
          Server-Zugriffsprotokollen verarbeitet: IP-Adresse, Zeitpunkt des Zugriffs, aufgerufene
          Adresse, HTTP-Statuscode und Browser-Kennung (User-Agent). Rechtsgrundlage ist Art. 6
          Abs. 1 lit. f DSGVO (berechtigtes Interesse an Betriebssicherheit und Fehleranalyse).
        </p>
        <p>
          <strong>3.3</strong> Diese Zugriffsprotokolle werden für maximal 14 Tage aufbewahrt und
          danach automatisch und unwiderruflich gelöscht.
        </p>
        <p>
          <strong>3.4</strong> Zum Schutz vor automatisierten Angriffen setzt Nighttrade eine
          Schutzsoftware ein, die bei auffälligen Zugriffsmustern (z. B. wiederholte Fehlanmeldungen)
          die betroffene IP-Adresse vorübergehend sperrt. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
        </p>
      `
    },
    {
      id: 'section-4',
      title: '§ 4 Registrierung und Benutzerkonto',
      defaultExpanded: false,
      content: `
        <p>
          <strong>4.1</strong> Für die Registrierung sind eine E-Mail-Adresse und ein Passwort
          erforderlich.
        </p>
        <p>
          <strong>4.2 Besonderer Umgang mit der E-Mail-Adresse:</strong> Die E-Mail-Adresse wird
          nicht dauerhaft im Klartext gespeichert. Sie wird mittels eines kryptografischen
          Verfahrens (HMAC-SHA256 mit serverseitigem Geheimwert) in einen nicht umkehrbaren
          Hash-Wert umgewandelt. Aus diesem Hash lässt sich die ursprüngliche E-Mail-Adresse nicht
          zurückgewinnen; er dient ausschließlich dazu, bei einer Anmeldung zu prüfen, ob die
          eingegebene Adresse zu einem bestehenden Konto gehört.
        </p>
        <p>
          <strong>4.3</strong> Für den Zeitraum des Registrierungs- bzw. Zugangsprozesses (z. B.
          Versand der Bestätigungs-E-Mail oder einer Benachrichtigung über einen frei gewordenen
          Alpha-Platz) wird die E-Mail-Adresse zusätzlich verschlüsselt (AES-256-GCM) vorgehalten —
          ausschließlich zu diesem Zweck. Sobald der Zweck erfüllt ist, wird diese verschlüsselte
          Kopie automatisch gelöscht. Danach besitzt Nighttrade keine technische Möglichkeit mehr,
          die E-Mail-Adresse eines Nutzers auszulesen (siehe auch § 15.3).
        </p>
        <p>
          <strong>4.4</strong> Das Passwort wird ausschließlich als Hash nach Industriestandard
          (Argon2id) gespeichert, niemals im Klartext.
        </p>
        <p>
          <strong>4.5</strong> Weitere Kontodaten: Zeitpunkt der Registrierung und letzter Änderung,
          Verifizierungsstatus, Kontorolle, Sperrstatus sowie — soweit im Registrierungsprozess
          erhoben — die Bestätigung des Mindestalters und die Zustimmung zu den Nutzungsregeln,
          jeweils mit Zeitstempel.
        </p>
      `
    },
    {
      id: 'section-5',
      title: '§ 5 Spieldaten',
      defaultExpanded: false,
      content: `
        <p>
          Im Rahmen der Nutzung werden spielbezogene Daten verarbeitet, insbesondere: Spielername
          (frei gewählt, kein Klarname erforderlich), Level, Erfahrungspunkte, Ruf,
          In-Game-Währungen, Gegenstände und Inventar, Talente, ausgeübte Tätigkeiten sowie
          wirtschaftliche Vorgänge innerhalb des Spiels. Diese Daten sind für die Bereitstellung des
          Spiels erforderlich (Art. 6 Abs. 1 lit. b DSGVO). Wirtschaftliche Vorgänge werden
          zusätzlich in einem unveränderlichen Protokoll gespeichert, das der Manipulations- und
          Betrugsprävention dient (Art. 6 Abs. 1 lit. f DSGVO); dieses Protokoll enthält keine
          Klarnamen und keine E-Mail-Adressen.
        </p>
      `
    },
    {
      id: 'section-6',
      title: '§ 6 Öffentliche Profilinformationen',
      defaultExpanded: false,
      content: `
        <p>
          Für andere Nutzer sichtbar sind ausschließlich spielbezogene Angaben wie Spielername,
          Level und spielinterne Auszeichnungen. Nicht öffentlich sichtbar sind: E-Mail-Adresse
          (bzw. deren Hash), Zugangs- und Zahlungsdaten.
        </p>
      `
    },
    {
      id: 'section-7',
      title: '§ 7 Cookies und lokale Speicherung im Browser',
      defaultExpanded: false,
      content: `
        <p>
          <strong>7.1</strong> Nighttrade verwendet ausschließlich technisch notwendige Cookies:
          ein Sitzungs-Cookie für die Anmeldung sowie ein Cookie zum Schutz vor Anfragenfälschung
          (CSRF). Diese sind für den Betrieb zwingend erforderlich (§ 25 Abs. 2 Nr. 2 TTDSG) und
          bedürfen keiner Einwilligung.
        </p>
        <p>
          <strong>7.2</strong> Zusätzlich speichert Nighttrade im Browser
          (LocalStorage/SessionStorage) rein technische, nicht personenbezogene Zustandsdaten —
          etwa den zuletzt geöffneten Spielbereich, Anzeigeentscheidungen bei Hinweisdialogen und
          die im Hinweisbanner getroffene Auswahl. Diese Daten verbleiben im Browser des Nutzers
          und werden nicht an den Server übertragen.
        </p>
      `
    },
    {
      id: 'section-8',
      title: '§ 8 Sitzungsverwaltung',
      defaultExpanded: false,
      content: `
        <p>
          <strong>8.1</strong> Nach der Anmeldung erhält der Nutzer ein Sitzungs-Cookie mit den
          Schutzmerkmalen <code>httpOnly</code> (nicht per Skript auslesbar),
          <code>SameSite=Strict</code> und — im Produktivbetrieb — <code>Secure</code>
          (Übertragung nur verschlüsselt).
        </p>
        <p>
          <strong>8.2</strong> Das Cookie enthält keinen personenbezogenen Inhalt, sondern einen
          zufälligen Sitzungsschlüssel. Die Sitzungsdaten selbst werden serverseitig gespeichert
          und nach Ablauf automatisch gelöscht.
        </p>
        <p>
          <strong>8.3</strong> Zu jeder Sitzung speichert Nighttrade zusätzlich einen
          <strong>Hash-Wert der IP-Adresse</strong> (SHA-256 mit serverseitigem Geheimwert — die
          Klartext-IP wird nicht gespeichert) sowie einen Hash-Wert der Browser-Kennung. Diese
          Werte dienen der Erkennung von Sitzungsmissbrauch (Art. 6 Abs. 1 lit. f DSGVO) und werden
          mit der Sitzung gelöscht.
        </p>
      `
    },
    {
      id: 'section-9',
      title: '§ 9 Sicherheit und Missbrauchsschutz',
      defaultExpanded: false,
      content: `
        <p>
          Zum Schutz der Nutzer und der Spielwirtschaft setzt Nighttrade serverseitige Validierung
          aller spielwirtschaftlich relevanten Vorgänge, Anfrage-Begrenzung (Rate-Limiting) und die
          in § 3.4 beschriebene automatisierte Zugriffsüberwachung ein. Bei Verdacht auf
          Manipulation können sicherheitsbezogene Ereignisse protokolliert und Konten gesperrt
          werden. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
        </p>
      `
    },
    {
      id: 'section-10',
      title: '§ 10 Zahlungsabwicklung (Mollie)',
      defaultExpanded: false,
      content: `
        <p>
          <strong>10.1</strong> Für den Erwerb kostenpflichtiger Inhalte wird der
          Zahlungsdienstleister Mollie B.V. (Amsterdam, Niederlande — EU) eingesetzt.
        </p>
        <p>
          <strong>10.2</strong> Die Eingabe und Verarbeitung vollständiger Zahlungsdaten (z. B.
          Kartendaten, Bankverbindung) erfolgt ausschließlich bei Mollie. Nighttrade selbst
          speichert lediglich: die von Mollie vergebene Transaktionsreferenz, den Zahlungsstatus,
          Betrag und Währung, die Art des gekauften Inhalts sowie die daraufhin gewährte
          spielinterne Gegenleistung.
        </p>
        <p>
          <strong>10.3</strong> Für die Verarbeitung durch Mollie gelten ergänzend die
          Datenschutzhinweise von Mollie
          (<a href="https://www.mollie.com/de/privacy" target="_blank" rel="noopener noreferrer">mollie.com/de/privacy</a>).
        </p>
        <p>
          <strong>10.4</strong> Zahlungsbezogene Aufzeichnungen unterliegen der gesetzlichen
          Aufbewahrungspflicht (§ 147 AO) und werden für die gesetzlich vorgeschriebene Dauer
          aufbewahrt — auch über eine Kontolöschung hinaus, dann jedoch ohne Verknüpfung zu einer
          identifizierbaren Person (§ 14.2).
        </p>
      `
    },
    {
      id: 'section-11',
      title: '§ 11 E-Mail-Versand (Resend)',
      defaultExpanded: false,
      content: `
        <p>
          Für den Versand technisch notwendiger E-Mails (Registrierungsbestätigung,
          Passwort-Zurücksetzung, sicherheitsrelevante Hinweise) wird der Dienst Resend eingesetzt.
          Übermittelt wird dabei die E-Mail-Adresse des Empfängers sowie der Inhalt der jeweiligen
          Nachricht. Es werden keine Marketing- oder Newsletter-E-Mails versendet.
        </p>
      `
    },
    {
      id: 'section-12',
      title: '§ 12 Twitch-Verknüpfung und Stream-Punkte (optional)',
      defaultExpanded: false,
      content: `
        <p>
          <strong>12.1</strong> Nutzer können ihr Konto freiwillig mit ihrem Twitch-Konto
          verknüpfen, um am Stream-Punkte-System teilzunehmen. Die Verknüpfung erfordert eine
          gesonderte, ausdrückliche Einwilligung vor Beginn des Verknüpfungsvorgangs (Art. 6
          Abs. 1 lit. a DSGVO) und kann jederzeit in den Kontoeinstellungen aufgehoben werden
          (Art. 7 Abs. 3 DSGVO).
        </p>
        <p>
          <strong>12.2</strong> Bei Verknüpfung werden verarbeitet: die Twitch-Nutzerkennung, der
          Twitch-Benutzername (Login-Name), Zugriffs- und Refresh-Token (verschlüsselt gespeichert,
          AES-256-GCM, niemals im Klartext), der Zeitpunkt der Verknüpfung sowie Zeitpunkt und
          Version der erteilten Einwilligung.
        </p>
        <p>
          <strong>12.3 Stream-Punkte-Erfassung:</strong> Ist ein Konto verknüpft, erfasst
          Nighttrade während der Übertragungen von Partner-Streamern die Anwesenheit des Nutzers
          im Twitch-Chat (regelmäßige Anwesenheitsprüfung), um daraus spielinterne Stream-Punkte zu
          berechnen. Jede Punktevergabe wird mit Quelle und Zeitpunkt protokolliert. Diese
          Erfassung findet ausschließlich für verknüpfte Konten statt und endet mit dem Aufheben
          der Verknüpfung.
        </p>
        <p>
          <strong>12.4 Drittlandübermittlung:</strong> Twitch ist ein Dienst der Twitch
          Interactive, Inc. (USA). Bei der Verknüpfung und der Teilnahme am Stream-Punkte-System
          können personenbezogene Daten in die USA übermittelt werden. Die Übermittlung erfolgt auf
          Grundlage der Standardvertragsklauseln der EU-Kommission (Art. 46 Abs. 2 lit. c DSGVO).
          Ergänzend gelten die Datenschutzbestimmungen von Twitch.
        </p>
      `
    },
    {
      id: 'section-13',
      title: '§ 13 Support-Funktionen',
      defaultExpanded: false,
      content: `
        <p>
          <strong>13.1 Fehlermeldungen (Bug-Reports):</strong> Nutzer können Fehler über ein
          Formular melden. Verarbeitet werden: Kategorie, Titel, Beschreibung, optionale
          Reproduktionsschritte, die automatisch ermittelte Browser-Kennung sowie optional ein vom
          Nutzer bereitgestellter Screenshot. Diese Angaben werden — ohne Klarnamen, ohne
          E-Mail-Adresse und ohne Bezug zu einem bestimmten Nutzerkonto, lediglich mit einer
          internen, rein technischen Report-Kennung — an einen vom Betreiber verwalteten Bereich
          auf der Plattform Discord übermittelt, um Fehler zentral zu bearbeiten. Screenshots
          werden nicht auf Nighttrade-Servern gespeichert. Discord wird von der Discord Inc. (USA)
          betrieben; bei der Übermittlung können Daten in die USA gelangen (Art. 46 DSGVO,
          Standardvertragsklauseln; ergänzend gelten die Datenschutzbestimmungen von Discord).
        </p>
        <p>
          <strong>13.2 Kontaktanfragen:</strong> Soweit ein Kontaktformular angeboten wird, werden
          Kategorie, Betreff und Nachricht verarbeitet. Eine vom Nutzer im Formular angegebene
          Antwort-E-Mail-Adresse wird ausschließlich zur Beantwortung der konkreten Anfrage
          verwendet und nicht dauerhaft gespeichert.
        </p>
      `
    },
    {
      id: 'section-14',
      title: '§ 14 Speicherdauer und Löschung',
      defaultExpanded: false,
      content: `
        <p>
          <strong>14.1</strong> Personenbezogene Daten werden gelöscht oder anonymisiert, sobald
          der Zweck ihrer Verarbeitung entfällt und keine gesetzliche Aufbewahrungspflicht
          entgegensteht.
        </p>
        <p>
          <strong>14.2 Kontolöschung:</strong> Nutzer können ihr Konto jederzeit in den
          Einstellungen löschen (Bestätigung durch Passworteingabe erforderlich). Dabei werden: der
          E-Mail-Hash durch einen zufälligen, keiner Person mehr zuordenbaren Wert ersetzt; der
          Passwort-Hash entfernt; sämtliche Bestätigungs- und Zurücksetzungs-Schlüssel gelöscht;
          eine ggf. noch vorhandene verschlüsselte E-Mail-Kopie gelöscht; alle aktiven Sitzungen
          beendet; und die Verknüpfung zwischen Konto und Spielfortschritt aufgehoben.
          Spielbezogene Daten (Fortschritt, spielwirtschaftliche Vorgänge) bleiben in vollständig
          anonymisierter Form — ohne jede Möglichkeit der Zuordnung zu einer Person — erhalten.
        </p>
        <p>
          <strong>14.3 Datensicherungen:</strong> Zur Ausfallsicherheit werden täglich
          automatisierte Sicherungen der Datenbank erstellt und für 14 Tage aufbewahrt, danach
          automatisch gelöscht. Gelöschte bzw. anonymisierte Daten können daher in Sicherungskopien
          für maximal weitere 14 Tage enthalten sein, werden dort aber nicht aktiv verarbeitet und
          mit dem regulären Sicherungszyklus endgültig entfernt.
        </p>
      `
    },
    {
      id: 'section-15',
      title: '§ 15 Rechte der Nutzer',
      defaultExpanded: false,
      content: `
        <p>
          <strong>15.1</strong> Nutzer haben nach Maßgabe der DSGVO das Recht auf: Auskunft
          (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung
          (Art. 18), Datenübertragbarkeit (Art. 20), Widerspruch (Art. 21) sowie Widerruf erteilter
          Einwilligungen (Art. 7 Abs. 3). Zuständige Aufsichtsbehörde für Beschwerden (Art. 77
          DSGVO) ist die Landesbeauftragte für Datenschutz und Informationsfreiheit
          Nordrhein-Westfalen (LDI NRW),
          <a href="https://www.ldi.nrw.de" target="_blank" rel="noopener noreferrer">www.ldi.nrw.de</a>.
        </p>
        <p>
          <strong>15.2</strong> Das Auskunfts- und Übertragbarkeitsrecht kann direkt in den
          Kontoeinstellungen ausgeübt werden („Daten exportieren" — maschinenlesbares
          JSON-Format). Zum Schutz vor Missbrauch ist der Export auf drei Abrufe pro 24 Stunden
          begrenzt.
        </p>
        <p>
          <strong>15.3 Hinweis zur E-Mail-Adresse:</strong> Aufgrund der in § 4.2–4.3 beschriebenen
          Datenminimierung liegt die E-Mail-Adresse bestehender Nutzer nur als nicht umkehrbarer
          Hash vor. Nighttrade kann sie daher weder auslesen noch herausgeben; im Rahmen einer
          Auskunft kann lediglich bestätigt werden, dass eine vom Nutzer genannte Adresse zu seinem
          Konto gehört.
        </p>
      `
    },
    {
      id: 'section-16',
      title: '§ 16 Mindestalter',
      defaultExpanded: false,
      content: `
        <p>
          Nighttrade richtet sich ausschließlich an Personen ab 18 Jahren. Die Registrierung setzt
          eine ausdrückliche Bestätigung des Mindestalters voraus.
        </p>
      `
    },
    {
      id: 'section-17',
      title: '§ 17 Empfänger und Dienstleister',
      defaultExpanded: false,
      content: `
        <p>Folgende Dienstleister werden eingesetzt:</p>
        <ul>
          <li><strong>netcup GmbH</strong> — Server-Hosting (Deutschland)</li>
          <li><strong>Resend</strong> — Transaktionale E-Mails (USA/EU — Verarbeitung gem. Art. 46 DSGVO)</li>
          <li><strong>Mollie B.V.</strong> — Zahlungsabwicklung (Niederlande, EU)</li>
          <li><strong>Twitch Interactive, Inc.</strong> — optionale Kontoverknüpfung (USA — Art. 46 DSGVO)</li>
          <li><strong>Discord Inc.</strong> — Übermittlung von Fehlermeldungen (USA — Art. 46 DSGVO)</li>
        </ul>
        <p>
          Mit Dienstleistern, die personenbezogene Daten im Auftrag verarbeiten, werden Verträge
          zur Auftragsverarbeitung gem. Art. 28 DSGVO geschlossen.
        </p>
      `
    },
    {
      id: 'section-18',
      title: '§ 18 Datenschutzbeauftragter',
      defaultExpanded: false,
      content: `
        <p>
          Eine gesetzliche Pflicht zur Benennung eines Datenschutzbeauftragten besteht nach
          aktuellem Stand nicht (§ 38 BDSG). Datenschutzanfragen richten Sie bitte an die in § 1
          genannte Kontaktadresse.
        </p>
      `
    },
    {
      id: 'section-19',
      title: '§ 19 Änderungen dieser Datenschutzerklärung',
      defaultExpanded: false,
      content: `
        <p>
          Diese Datenschutzerklärung wird bei Einführung neuer Funktionen oder bei rechtlichen
          Änderungen angepasst. Es gilt die jeweils auf dieser Seite veröffentlichte Fassung.
        </p>
      `
    }
  ];

  renderLegalPage(
    'Datenschutzerklärung',
    '<em>Stand: 09.07.2026 · Version 1.0</em>',
    sections
  );
}
